import { PRODUCT_NAME } from "@mailhand/core/brand";
import { FORWARD_PROVIDER_LABELS } from "@mailhand/core/screen";
import type { Action, Inbox } from "@mailhand/core/schemas";
import { emailSchema } from "@mailhand/core/schemas";
import { Fragment, type SubmitEvent, type ReactNode, useId, useState } from "react";
import { Link } from "../../app/router.js";
import { useApi } from "../../app/services.js";
import { isSetupComplete, useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { usePoll } from "../../lib/usePoll.js";
import { Badge, Button, Card, CopyButton, Eyebrow, Field, Icon, Input, PageHead, Textarea, type Tone } from "../../ui/index.js";
import { InboxCreateForm } from "../shared/InboxCreateForm.js";
import { INSTRUCTIONS_EXAMPLE, INSTRUCTIONS_HINT } from "../shared/instructions.js";
import { usePatchTenant } from "../settings/usePatchTenant.js";
import { ActionEditor } from "../actions/ActionEditor.js";
import { ActionRow, orderedActions } from "../actions/ActionRow.js";
import { ForwardingGuide } from "./ForwardingGuide.js";
import { useSkippedSteps } from "./skippedSteps.js";

const POLL_MS = 5000;
const TEAM_ACTION_NAME = "Send to the team";
const TEAM_ACTION_DESCRIPTION = "Anything that needs a person: questions the instructions do not answer, complaints, wholesale, press, and partnerships.";
const TEAM_DAILY_CAP = 500;

type StepStatus = "todo" | "done" | "skipped";

const STATUS_BADGE: Record<Exclude<StepStatus, "todo">, { label: string; tone: Tone }> = {
  done: { label: "Done", tone: "green" },
  skipped: { label: "Skipped", tone: "grey" },
};

interface StepProps {
  number: number;
  title: string;
  status: StepStatus;
  current: boolean;
  /** Present only on an optional step: it adds the Skip button. */
  onSkip?: () => void;
  children: ReactNode;
}

/** A finished or skipped step starts collapsed; the member can open it again from its header. */
function Step({ number, title, status, current, onSkip, children }: StepProps) {
  const [toggled, setToggled] = useState<{ status: StepStatus; open: boolean } | null>(null);
  const open = toggled?.status === status ? toggled.open : status === "todo";
  const bodyId = useId();
  const badge = status === "todo" ? (onSkip === undefined ? null : { label: "Optional", tone: "grey" as const }) : STATUS_BADGE[status];
  return (
    <Card className={["step", status === "done" ? "is-done" : "", current ? "is-current" : ""].join(" ").trim()}>
      <button type="button" className="step-head" aria-expanded={open} aria-controls={bodyId} onClick={() => { setToggled({ status, open: !open }); }}>
        <span className="step-number mono">{status === "done" ? <Icon name="check" size={14} /> : String(number).padStart(2, "0")}</span>
        <h2 className="card-title">{title}</h2>
        {badge === null ? null : <Badge tone={badge.tone}>{badge.label}</Badge>}
        <span className="step-chevron" aria-hidden="true">
          <Icon name="chevronDown" size={16} />
        </span>
      </button>
      {open ? (
        <div className="step-body" id={bodyId}>
          {children}
          {onSkip === undefined || status !== "todo" ? null : (
            <div className="button-row">
              <Button variant="ghost" onClick={onSkip}>
                Skip
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </Card>
  );
}

/** True once the first email reaches the tenant, which proves the forward works. */
function useFirstEmail(watching: boolean): boolean {
  const api = useApi();
  const [arrived, setArrived] = useState(false);
  usePoll(
    async () => {
      const list = await api.call("messagesList", { query: { limit: 1 } });
      setArrived(list.messages.length > 0);
    },
    POLL_MS,
    watching && !arrived,
    true,
  );
  return arrived;
}

function optionalStatus(done: boolean, skipped: boolean): StepStatus {
  if (done) return "done";
  return skipped ? "skipped" : "todo";
}

export function SetupScreen() {
  const { view, actions } = useTenant();
  const { skipped, skip } = useSkippedSteps(view.tenant.id);
  const inbox = view.inboxes[0];
  const emailArrived = useFirstEmail(inbox !== undefined);
  const complete = isSetupComplete(view, actions);

  const statuses = {
    address: inbox === undefined ? "todo" : "done",
    forward: optionalStatus(emailArrived, skipped.has("forward")),
    test: optionalStatus(emailArrived, skipped.has("test")),
    instructions: view.tenant.instructions.trim() === "" ? "todo" : "done",
    team: teamAction(actions) === undefined ? "todo" : "done",
    action: optionalStatus(actions.some((action) => action.type === "webhook"), skipped.has("action")),
    domain: optionalStatus(view.domains.some((domain) => domain.state === "ready"), skipped.has("domain")),
  } as const satisfies Record<string, StepStatus>;
  const current = Object.entries(statuses).find(([, status]) => status === "todo")?.[0];

  return (
    <>
      <PageHead title={complete ? "Setup complete" : "Setup"} />
      <div className="steps">
        <Step number={1} title="Get your forwarding address" status={statuses.address} current={current === "address"}>
          {inbox === undefined ? <InboxCreateForm onCreated={() => undefined} /> : <AddressLine inbox={inbox} />}
        </Step>
        <Step number={2} title="Forward your email and confirm it" status={statuses.forward} current={current === "forward"} onSkip={() => { skip("forward"); }}>
          {inbox === undefined ? <NeedsAddress /> : <ForwardStep inbox={inbox} />}
        </Step>
        <Step number={3} title="Send a test email" status={statuses.test} current={current === "test"} onSkip={() => { skip("test"); }}>
          {inbox === undefined ? <NeedsAddress /> : <TestEmail address={inbox.address} arrived={emailArrived} />}
        </Step>
        <Step number={4} title={`Tell ${PRODUCT_NAME} about your business`} status={statuses.instructions} current={current === "instructions"}>
          <InstructionsStep />
        </Step>
        <Step number={5} title="Where should email go when a person is needed?" status={statuses.team} current={current === "team"}>
          <TeamAddressStep />
        </Step>
        <Step number={6} title="Add your first action" status={statuses.action} current={current === "action"} onSkip={() => { skip("action"); }}>
          <FirstActionStep />
        </Step>
        <Step number={7} title="Want replies from your own domain?" status={statuses.domain} current={current === "domain"} onSkip={() => { skip("domain"); }}>
          <p className="muted">Replies come from a shared {PRODUCT_NAME} address until you verify your domain. First add one TXT record to prove the domain is yours, then three CNAME records for sending.</p>
          <div className="button-row">
            <Link to="/settings#domain" className="btn btn-secondary btn-md">
              Set up a sending domain
            </Link>
          </div>
        </Step>
      </div>
      {complete ? (
        <div className="setup-finish">
          <Link to="/activity" className="btn btn-primary btn-md">
            Setup Complete: View Dashboard <Icon name="arrowRight" size={15} />
          </Link>
        </div>
      ) : null}
    </>
  );
}

function NeedsAddress() {
  return <p className="muted">Get your forwarding address in step 1 first.</p>;
}

function AddressLine({ inbox }: { inbox: Inbox }) {
  return (
    <div className="forward-address">
      <span className="mono">{inbox.address}</span>
      <CopyButton value={inbox.address} />
    </div>
  );
}

function ForwardStep({ inbox }: { inbox: Inbox }) {
  return (
    <>
      <ForwardAddress inbox={inbox} />
      <ForwardConfirmation address={inbox.address} />
    </>
  );
}

function FirstActionStep() {
  const { actions } = useTenant();
  const [editing, setEditing] = useState<Action["id"] | null>(null);
  return (
    <>
      <p className="muted">
        Here are the actions that come with {PRODUCT_NAME} out of the box. You can delete or edit them here, and add more if you like. You can change actions anytime from
        the Actions page.
      </p>
      <ul className="action-list">
        {orderedActions(actions).map((action) => (
          <Fragment key={action.id}>
            <ActionRow action={action} open={{ kind: "inline", expanded: editing === action.id, onToggle: () => { setEditing(editing === action.id ? null : action.id); } }} />
            {editing === action.id ? (
              <li className="action-inline-editor">
                <ActionEditor action={action} onDone={() => { setEditing(null); }} />
              </li>
            ) : null}
          </Fragment>
        ))}
      </ul>
      <div className="button-row">
        <Link to="/actions/new?from=setup" className="btn btn-secondary btn-md">
          Add Another Action <Icon name="arrowRight" size={15} />
        </Link>
      </div>
    </>
  );
}

function ForwardAddress({ inbox }: { inbox: Inbox }) {
  return (
    <div className="stack">
      <p className="muted">Your {PRODUCT_NAME} address. Forward your support email here:</p>
      <div className="forward-address">
        <span className="mono">{inbox.address}</span>
        <CopyButton value={inbox.address} />
      </div>
      <ForwardingGuide address={inbox.address} />
    </div>
  );
}

function ForwardConfirmation({ address }: { address: string }) {
  const api = useApi();
  const [inbox, setInbox] = useState<Inbox | null>(null);
  const confirmation = inbox?.forwardConfirmation;
  usePoll(
    async () => {
      setInbox(await api.call("inboxGet", { params: { address } }));
    },
    POLL_MS,
    confirmation === undefined,
    true,
  );

  if (confirmation === undefined) {
    return (
      <div className="stack">
        <Eyebrow live>Watching for a confirmation</Eyebrow>
        <p className="muted">Gmail, Yahoo, and Cloudflare Email Routing send a confirmation to the new address. It appears here within seconds. Outlook and Google Workspace routing need no confirmation; skip this step.</p>
      </div>
    );
  }
  return (
    <div className="stack">
      <Eyebrow>From {FORWARD_PROVIDER_LABELS[confirmation.provider]}</Eyebrow>
      {confirmation.code === undefined ? null : (
        <div className="forward-address">
          <span className="mono big-code">{confirmation.code}</span>
          <CopyButton value={confirmation.code} />
        </div>
      )}
      {confirmation.link === undefined ? null : (
        <div className="button-row">
          <a className="btn btn-accent btn-md" href={confirmation.link} target="_blank" rel="noreferrer">
            Confirm <Icon name="external" size={14} />
          </a>
        </div>
      )}
    </div>
  );
}

function TestEmail({ address, arrived }: { address: string; arrived: boolean }) {
  return (
    <div className="stack">
      <p className="muted">
        From another account, send an email to your support address. It forwards to <span className="mono">{address}</span>.
      </p>
      <div className={`live-status${arrived ? " is-live" : ""}`}>
        <span className="live-dot" aria-hidden="true" />
        {arrived ? (
          <span>
            Email is arriving. <Link to="/activity">See it in Activity</Link>
          </span>
        ) : (
          <span>Waiting for the first email…</span>
        )}
      </div>
    </div>
  );
}

function InstructionsStep() {
  const { view } = useTenant();
  const { busy, patch } = usePatchTenant();
  const [instructions, setInstructions] = useState(view.tenant.instructions);

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        void patch({ instructions }, "Instructions saved.");
      }}
    >
      <Field label="Instructions" hint={INSTRUCTIONS_HINT}>
        {({ id, describedBy }) => (
          <Textarea id={id} rows={9} maxLength={4000} placeholder={INSTRUCTIONS_EXAMPLE} value={instructions} aria-describedby={describedBy} onChange={(event) => { setInstructions(event.target.value); }} />
        )}
      </Field>
      <div className="button-row">
        <Button type="submit" variant="primary" busy={busy} disabled={instructions.trim() === ""}>
          Save instructions
        </Button>
      </div>
    </form>
  );
}

function teamAction(actions: readonly Action[]): Extract<Action, { type: "forward" }> | undefined {
  return actions.find((action): action is Extract<Action, { type: "forward" }> => action.type === "forward");
}

function TeamAddressStep() {
  const api = useApi();
  const { actions, setActions } = useTenant();
  const task = useTask();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const existing = teamAction(actions);

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const address = emailSchema.safeParse(email.trim());
    if (!address.success) {
      setError("Type the address of a person or a team inbox.");
      return;
    }
    setError(undefined);
    await task.run(async () => {
      const created = await api.call("actionCreate", {
        body: {
          spec: {
            type: "forward",
            name: TEAM_ACTION_NAME,
            description: TEAM_ACTION_DESCRIPTION,
            enabled: true,
            followUp: false,
            mode: "auto",
            dailyCap: TEAM_DAILY_CAP,
            inputs: [{ name: "to", type: "email", required: true, source: "constant", value: address.data }],
          },
        },
      });
      setActions([...actions, created]);
    }, `Email that needs a person goes to ${address.data}.`);
  }

  if (existing !== undefined) {
    const to = existing.inputs[0];
    return (
      <p className="muted">
        "{existing.name}" forwards to <span className="mono">{to.value}</span>.{" "}
        <Link to={`/actions/${existing.id}`}>Edit</Link>
      </p>
    );
  }

  return (
    <form className="inline-form" onSubmit={(event) => void submit(event)} noValidate>
      <Field label="Team address" hint={`Creates the action "${TEAM_ACTION_NAME}".`} error={error}>
        {({ id, describedBy, invalid }) => (
          <Input id={id} type="email" inputMode="email" placeholder="team@yourbusiness.com" value={email} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setEmail(event.target.value); }} />
        )}
      </Field>
      <Button type="submit" variant="primary" busy={task.busy}>
        Save
      </Button>
    </form>
  );
}
